import {
  MessageGroup,
  UserAvatar,
  type ReactionOption,
} from "@somethings-ui/web";
import { CheckCheck, Copy, Reply } from "lucide-react";
import { type ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { getDisplayText, MENTOR_LANGUAGE } from "@/lib/sendbird/translation";
import type { AttentionLightboxMedia } from "./attention-lightbox";
import type { AttentionSurfaceCapabilities } from "./attention-capabilities";
import {
  AttentionGif,
  AttentionMedia,
} from "./attention-thread-media";
import type {
  AttentionBubbleItem,
  AttentionCenteredContent,
  AttentionReaction,
  AttentionRow,
} from "./attention-view";

type AttentionActivityCentered = Extract<
  AttentionCenteredContent,
  { type: "activity" }
>;
type AttentionEntitySharedCentered = Extract<
  AttentionCenteredContent,
  { type: "entityShared" }
>;
type AttentionEntityInteractionBubble = Extract<
  AttentionBubbleItem,
  { type: "entityInteraction" }
>;
type AttentionTextBubble = Extract<AttentionBubbleItem, { type: "text" }>;

/**
 * Injection seam for domain-coupled activity/entity renderers. Surfaces that
 * omit these fall back to plain centered/text labels.
 */
export interface AttentionSpecialContentRenderers {
  activity?: (content: AttentionActivityCentered) => ReactNode;
  entityShared?: (content: AttentionEntitySharedCentered) => ReactNode;
  entityInteraction?: (item: AttentionEntityInteractionBubble) => ReactNode;
  textBadge?: (item: AttentionTextBubble) => ReactNode;
}

export interface AttentionThreadCallbacks {
  onReply?: (messageId: string) => void;
  onToggleReaction?: (messageId: string, emoji: string) => void;
  onCopy?: (text: string) => void;
}

export interface AttentionRowViewProps extends AttentionThreadCallbacks {
  row: AttentionRow;
  capabilities: AttentionSurfaceCapabilities;
  reactionChoices: ReactionOption[];
  openId: string | null;
  setOpenId: (value: string | null) => void;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
  specialContent?: AttentionSpecialContentRenderers;
}

export function AttentionRowView({
  row,
  capabilities,
  reactionChoices,
  openId,
  setOpenId,
  onReply,
  onToggleReaction,
  onCopy,
  onMediaSettled,
  onOpenMedia,
  specialContent,
}: AttentionRowViewProps) {
  switch (row.type) {
    case "dateSeparator":
      return <MessageGroup.DateSeparator label={row.label} />;
    case "centered":
      return (
        <AttentionCenteredRow
          centered={row.centered}
          specialContent={specialContent}
        />
      );
    case "group": {
      const group = (
        <MessageGroup direction={row.direction}>
          {row.showSenderHeader && row.senderLabel ? (
            <span className="px-1 font-body text-body-4 font-medium text-text-secondary">
              {row.senderLabel}
            </span>
          ) : null}
          {row.items.map((item) => (
            <AttentionBubbleRow
              key={item.id}
              item={item}
              direction={row.direction}
              capabilities={capabilities}
              reactionChoices={reactionChoices}
              openId={openId}
              setOpenId={setOpenId}
              onReply={onReply}
              onToggleReaction={onToggleReaction}
              onCopy={onCopy}
              onMediaSettled={onMediaSettled}
              onOpenMedia={onOpenMedia}
              specialContent={specialContent}
            />
          ))}
          {row.timestampLabel.length > 0 || row.showReadReceipt ? (
            <MessageGroup.Metadata>
              {row.timestampLabel}
              {row.showReadReceipt ? (
                <span title="Read" className="inline-flex text-text-tertiary">
                  <CheckCheck className="size-4 shrink-0" aria-hidden />
                </span>
              ) : null}
            </MessageGroup.Metadata>
          ) : null}
        </MessageGroup>
      );
      // MessageGroup has no author slot; only incoming group runs show the gutter.
      if (!row.showSenderHeader) return group;
      return (
        <div className="flex items-start gap-2">
          <UserAvatar
            size={24}
            name={row.senderLabel ?? ""}
            imageUrl={row.senderAvatarUrl ?? undefined}
            showBorder={false}
            className="mt-0.5"
          />
          {group}
        </div>
      );
    }
    default: {
      const exhaustive: never = row;
      return exhaustive;
    }
  }
}

interface AttentionBubbleRowProps extends AttentionThreadCallbacks {
  item: AttentionBubbleItem;
  direction: "incoming" | "outgoing";
  capabilities: AttentionSurfaceCapabilities;
  reactionChoices: ReactionOption[];
  openId: string | null;
  setOpenId: (value: string | null) => void;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
  specialContent?: AttentionSpecialContentRenderers;
}

export function AttentionBubbleRow({
  item,
  direction,
  capabilities,
  reactionChoices,
  openId,
  setOpenId,
  onReply,
  onToggleReaction,
  onCopy,
  onMediaSettled,
  onOpenMedia,
  specialContent,
}: AttentionBubbleRowProps) {
  const framed = isFramedBubble(item, specialContent);
  // Shared openId so one 3-dot trigger opens reaction picker + actions together.
  const isOpen = openId === item.id;
  const interactive =
    capabilities.canReact &&
    (onReply !== undefined ||
      onToggleReaction !== undefined ||
      onCopy !== undefined);

  const [showOriginal, setShowOriginal] = useState(false);
  const translation =
    item.type === "text" || item.type === "supervision"
      ? {
          original: item.text,
          ...getDisplayText(
            { message: item.text, translations: item.translations ?? {} },
            MENTOR_LANGUAGE,
          ),
        }
      : null;
  const renderedText = translation
    ? translation.isTranslated && showOriginal
      ? translation.original
      : translation.text
    : undefined;

  const textBadge =
    item.type === "text" ? specialContent?.textBadge?.(item) : null;

  const bubble = (
    <MessageGroup.Bubble
      className={cn(
        "text-body-2",
        framed && "p-1",
        item.sendState === "pending" && "opacity-60",
      )}
    >
      <AttentionBubbleBody
        item={item}
        renderedText={renderedText}
        onMediaSettled={onMediaSettled}
        onOpenMedia={onOpenMedia}
        specialContent={specialContent}
      />
    </MessageGroup.Bubble>
  );

  return (
    <>
      {item.quote ? (
        <MessageGroup.Quote
          label={item.quote.senderLabel}
          thumbnail={
            item.quote.thumbnailUrl ? (
              <img
                src={item.quote.thumbnailUrl}
                alt=""
                className="size-full object-cover"
              />
            ) : undefined
          }
        >
          {item.quote.text}
        </MessageGroup.Quote>
      ) : null}

      {textBadge}

      <MessageGroup.Row>
        {interactive && onToggleReaction ? (
          <MessageGroup.ReactionPicker
            open={isOpen}
            onOpenChange={(open) => setOpenId(open ? item.id : null)}
          >
            <MessageGroup.ReactionPicker.Anchor asChild>
              {bubble}
            </MessageGroup.ReactionPicker.Anchor>
            <MessageGroup.ReactionPicker.Content
              side="top"
              align={direction === "outgoing" ? "end" : "start"}
              emojis={reactionChoices}
              onSelect={(key) => {
                onToggleReaction(item.id, key);
                setOpenId(null);
              }}
            />
          </MessageGroup.ReactionPicker>
        ) : (
          bubble
        )}

        {interactive ? (
          <span className={direction === "outgoing" ? "order-first flex" : "flex"}>
            <MessageGroup.ActionsMenu
              open={isOpen}
              onOpenChange={(open) => setOpenId(open ? item.id : null)}
            >
              <MessageGroup.ActionsMenu.Trigger>
                <MessageGroup.ActionTrigger open={isOpen} />
              </MessageGroup.ActionsMenu.Trigger>
              <MessageGroup.ActionsMenu.Content
                side="bottom"
                align={direction === "outgoing" ? "start" : "end"}
              >
                {onReply ? (
                  <MessageGroup.ActionsMenu.Item
                    icon={<Reply className="size-4" />}
                    onSelect={() => onReply(item.id)}
                  >
                    Reply
                  </MessageGroup.ActionsMenu.Item>
                ) : null}
                {onCopy && bubbleCopyText(item) !== null ? (
                  <MessageGroup.ActionsMenu.Item
                    icon={<Copy className="size-4" />}
                    onSelect={() => onCopy(bubbleCopyText(item) ?? "")}
                  >
                    Copy
                  </MessageGroup.ActionsMenu.Item>
                ) : null}
              </MessageGroup.ActionsMenu.Content>
            </MessageGroup.ActionsMenu>
          </span>
        ) : null}
      </MessageGroup.Row>

      {translation?.isTranslated ? (
        <button
          type="button"
          onClick={() => setShowOriginal((prev) => !prev)}
          className="px-1 font-body text-body-4 text-text-tertiary hover:underline"
        >
          {showOriginal ? "Show translation" : "Translated · Show original"}
        </button>
      ) : null}

      {item.reactions.length > 0 ? (
        <MessageGroup.Reactions>
          {item.reactions.map((reaction) => (
            <AttentionReactionPill
              key={reaction.emoji}
              reaction={reaction}
              onClick={
                onToggleReaction
                  ? () => onToggleReaction(item.id, reaction.emoji)
                  : undefined
              }
            />
          ))}
        </MessageGroup.Reactions>
      ) : null}
    </>
  );
}

export function AttentionReactionPill({
  reaction,
  onClick,
}: {
  reaction: AttentionReaction;
  onClick?: () => void;
}) {
  return (
    <MessageGroup.ReactionPill
      emoji={reaction.emoji}
      count={reaction.count}
      active={reaction.reactedBySelf}
      icon={
        reaction.url ? (
          <img
            src={reaction.url}
            alt=""
            className="size-4 object-contain"
          />
        ) : undefined
      }
      label={reaction.emoji}
      onClick={onClick}
    />
  );
}

function AttentionBubbleBody({
  item,
  renderedText,
  onMediaSettled,
  onOpenMedia,
  specialContent,
}: {
  item: AttentionBubbleItem;
  renderedText?: string;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
  specialContent?: AttentionSpecialContentRenderers;
}) {
  switch (item.type) {
    case "text":
    case "supervision":
      return <>{renderedText ?? item.text}</>;
    case "unsupported":
      return (
        <span className="italic text-text-tertiary">
          {item.text.trim().length > 0 ? item.text : "Unsupported message"}
        </span>
      );
    case "entityInteraction":
      return specialContent?.entityInteraction ? (
        <>{specialContent.entityInteraction(item)}</>
      ) : (
        <>{item.text}</>
      );
    case "gif":
      return (
        <AttentionGif
          url={item.url}
          onMediaSettled={onMediaSettled}
          onOpenMedia={onOpenMedia}
        />
      );
    case "media":
      return (
        <AttentionMedia
          item={item}
          onMediaSettled={onMediaSettled}
          onOpenMedia={onOpenMedia}
        />
      );
    case "hidden":
      return (
        <span className="italic text-text-tertiary">Message hidden</span>
      );
    default: {
      const exhaustive: never = item;
      return exhaustive;
    }
  }
}

export function AttentionCenteredRow({
  centered,
  specialContent,
}: {
  centered: AttentionCenteredContent;
  specialContent?: AttentionSpecialContentRenderers;
}) {
  if (centered.type === "activity" && specialContent?.activity) {
    return <>{specialContent.activity(centered)}</>;
  }
  if (centered.type === "entityShared" && specialContent?.entityShared) {
    return <>{specialContent.entityShared(centered)}</>;
  }
  return (
    <div className="flex justify-center px-6">
      <span className="max-w-sm rounded-full bg-surfaces-z2 px-3 py-1.5 text-center font-body text-body-4 leading-[1.4] text-text-tertiary">
        {centeredLabel(centered)}
      </span>
    </div>
  );
}

export function centeredLabel(centered: AttentionCenteredContent): string {
  switch (centered.type) {
    case "system":
      return centered.text;
    case "activity":
      return centered.text.length > 0 ? centered.text : "Activity update";
    case "entityShared":
      return centered.text.length > 0 ? centered.text : "Shared an update";
    case "game":
      return centered.phase === "start" ? "Game started" : "Game ended";
    case "takedown":
      return "Message removed";
    default: {
      const exhaustive: never = centered;
      return exhaustive;
    }
  }
}

export function isFramedBubble(
  item: AttentionBubbleItem,
  specialContent?: AttentionSpecialContentRenderers,
): boolean {
  if (item.type === "gif") return true;
  if (item.type === "media") {
    return item.media.mediaType === "image" || item.media.mediaType === "video";
  }
  // Rendered interaction thumbnail is framed; plain text fallback is not.
  if (item.type === "entityInteraction") {
    return specialContent?.entityInteraction !== undefined;
  }
  return false;
}

export function bubbleCopyText(item: AttentionBubbleItem): string | null {
  switch (item.type) {
    case "text":
    case "supervision":
    case "unsupported":
    case "entityInteraction":
      return item.text;
    case "media":
      return item.caption;
    case "gif":
    case "hidden":
      return null;
    default: {
      const exhaustive: never = item;
      return exhaustive;
    }
  }
}
